import React, { useEffect, useState } from "react";
import { FaClock, FaExclamationTriangle } from "react-icons/fa";

type ExamTimerProps = {
  endTime: string; // ISO datetime string
  onTimeUp: () => void;
  isSubmitted?: boolean;
};

const ExamTimer: React.FC<ExamTimerProps> = ({
  endTime,
  onTimeUp,
  isSubmitted = false,
}) => {
  const [timeRemaining, setTimeRemaining] = useState<number>(0);
  const [hasExpired, setHasExpired] = useState(false);

  useEffect(() => {
    const calculateTimeRemaining = () => {
      const now = new Date().getTime();
      const end = new Date(endTime).getTime();
      const diff = end - now;

      if (diff <= 0) {
        setTimeRemaining(0);
        if (!hasExpired && !isSubmitted) {
          setHasExpired(true);
          onTimeUp();
        }
        return 0;
      }

      return Math.floor(diff / 1000); // Convert to seconds
    };

    // Calculate initial time
    const initial = calculateTimeRemaining();
    setTimeRemaining(initial);

    // Update every second
    const interval = setInterval(() => {
      const remaining = calculateTimeRemaining();
      setTimeRemaining(remaining);
    }, 1000);

    return () => clearInterval(interval);
  }, [endTime, onTimeUp, hasExpired, isSubmitted]);

  const formatTime = (seconds: number) => {
    const hours = Math.floor(seconds / 3600);
    const minutes = Math.floor((seconds % 3600) / 60);
    const secs = seconds % 60;

    return {
      hours: hours.toString().padStart(2, "0"),
      minutes: minutes.toString().padStart(2, "0"),
      seconds: secs.toString().padStart(2, "0"),
    };
  };

  const time = formatTime(timeRemaining);
  const totalMinutes = Math.floor(timeRemaining / 60);

  // Determine color based on time remaining
  const getTimerColor = () => {
    if (timeRemaining === 0) {
      return "bg-red-600";
    } else if (totalMinutes < 5) {
      return "bg-red-500";
    } else if (totalMinutes < 10) {
      return "bg-orange-500";
    } else if (totalMinutes < 15) {
      return "bg-yellow-500";
    } else {
      return "bg-[#009fe3]";
    }
  };

  const getTextColor = () => {
    if (timeRemaining === 0) {
      return "text-red-600";
    } else if (totalMinutes < 5) {
      return "text-red-600";
    } else if (totalMinutes < 10) {
      return "text-orange-600";
    } else if (totalMinutes < 15) {
      return "text-yellow-600";
    } else {
      return "text-[#009fe3]";
    }
  };

  const shouldAnimate = totalMinutes < 5 && timeRemaining > 0;

  if (isSubmitted) {
    return (
      <div className="flex items-center gap-3 px-4 py-3 bg-green-50 border border-green-200 rounded-lg">
        <FaClock className="text-green-600 text-xl" />
        <div>
          <div className="text-sm font-semibold text-green-800">
            Exam Submitted
          </div>
          <div className="text-xs text-green-600">
            Time is no longer tracked
          </div>
        </div>
      </div>
    );
  }

  return (
    <div
      className={`flex items-center gap-3 px-4 py-3 rounded-lg border-2 transition-all duration-300 ${
        timeRemaining === 0
          ? "bg-red-50 border-red-300"
          : totalMinutes < 5
          ? "bg-red-50 border-red-300"
          : totalMinutes < 10
          ? "bg-orange-50 border-orange-300"
          : totalMinutes < 15
          ? "bg-yellow-50 border-yellow-300"
          : "bg-blue-50 border-blue-300"
      } ${shouldAnimate ? "animate-pulse" : ""}`}
    >
      <div
        className={`w-12 h-12 ${getTimerColor()} rounded-lg flex items-center justify-center ${
          shouldAnimate ? "animate-pulse" : ""
        }`}
      >
        {timeRemaining === 0 ? (
          <FaExclamationTriangle className="text-white text-xl" />
        ) : (
          <FaClock className="text-white text-xl" />
        )}
      </div>

      <div className="flex-1">
        <div className="text-sm font-semibold text-gray-700">
          {timeRemaining === 0
            ? "Time's Up!"
            : totalMinutes < 5
            ? "Hurry! Time Running Out"
            : totalMinutes < 10
            ? "Less Than 10 Minutes Left"
            : "Time Remaining"}
        </div>
        <div className={`text-2xl font-bold ${getTextColor()} font-mono`}>
          {time.hours !== "00" && (
            <>
              <span>{time.hours}</span>
              <span className="mx-0.5">:</span>
            </>
          )}
          <span>{time.minutes}</span>
          <span className="mx-0.5">:</span>
          <span>{time.seconds}</span>
        </div>
      </div>

      {/* Warning Message */}
      {totalMinutes < 5 && timeRemaining > 0 && (
        <div className="text-xs text-red-600 font-semibold">
          <FaExclamationTriangle className="inline mr-1" />
          Less than 5 minutes!
        </div>
      )}

      {timeRemaining === 0 && (
        <div className="text-xs text-red-600 font-semibold">
          Auto-submitting...
        </div>
      )}
    </div>
  );
};

export default ExamTimer;
